import React, { useState, useEffect } from "react";
import "./TradeModal.css";
import API_URL from "./config";

const DRILLS = [
  {
    skill: "defense",
    label: "Defense",
    icon: "🛡️",
    subtitle: "Good defense lowers the opponent's score",
  },
  {
    skill: "shooting",
    label: "Shooting",
    icon: "🏀",
    subtitle: "Shooting practice is impactful as long as you score",
  },
  {
    skill: "dribbling",
    label: "Dribbling",
    icon: "⛹️‍♂️",
    subtitle: "Improve ball-handling and scoring efficiency",
  },
  {
    skill: "passing",
    label: "Passing",
    icon: "🤝",
    subtitle: "Teamwork is essential on both offense and defense",
  },
];

export default function PracticeModal({ saveId, teamId, stamina, onClose, onPracticeComplete }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [hoveredDrill, setHoveredDrill] = useState(null);
  const [practiceResult, setPracticeResult] = useState(null);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user._id;

  const skillIcons = {
    defense: "🛡️",
    shooting: "🏀",
    dribbling: "⛹️‍♂️",
    passing: "🤝",
  };

  const accentColor = "var(--color-primary)";

  const handleCloseModal = () => {
    if (practiceResult && onPracticeComplete) {
      onPracticeComplete(practiceResult);
    }
    onClose();
  };

  // Determine if this is a max or min roll
  const getValueStatus = () => {
    if (!practiceResult) return "normal";
    
    const { bonusType, bonusValue, skill, success } = practiceResult;
    
    if (bonusType === "opponent_score_reduction") {
      if (bonusValue === 10) return "max";
      if (bonusValue === 5) return "min";
    } else if (bonusType === "team_score_bonus") {
      if (skill === "shooting") {
        if (bonusValue === 13) return "max";
        if (bonusValue === 0) return "min";
      } else if (skill === "passing") {
        if (bonusValue === 10) return "max";
        if (bonusValue === 5) return "min";
      }
    } else if (bonusType === "ppp_multiplier") {
      if (bonusValue === 1.1) return "max";
      if (bonusValue === 1.05) return "min";
    }
    return "normal";
  };

  const valueStatus = getValueStatus();

  // Render different bonus displays based on type
  const renderBonusDisplay = () => {
    if (!practiceResult) return null;
    
    const { bonusType, bonusValue, skill, success, message, statLabel } = practiceResult;
    
    const getAnimationStyle = () => {
      if (valueStatus === "max") {
        return "glow-perfect";
      } else if (valueStatus === "min") {
        return "glow-poor";
      }
      return "slideIn";
    };

    if (bonusType === "opponent_score_reduction") {
      return (
        <div className="bonus-display" style={{
          background: "rgba(244, 67, 54, 0.1)",
          border: `2px solid #f44336`,
          borderRadius: "12px",
          padding: "2rem",
          marginTop: "1rem",
          textAlign: "center",
          animation: `${getAnimationStyle()} 0.6s ease-out`
        }}>
          <div style={{
            fontSize: "2.5rem",
            fontWeight: "bold",
            color: "#f44336",
            marginBottom: "0.5rem"
          }}>
            -{bonusValue}% Opponent Score
          </div>
          <div style={{
            color: "var(--color-text-secondary)",
            fontSize: "0.95rem",
            lineHeight: "1.5"
          }}>
            Your defense has locked in. The opponent will have {bonusValue}% of their score reduced next game!
          </div>
        </div>
      );
    } else if (bonusType === "team_score_bonus") {
      if (skill === "shooting" && !success) {
        // Shooting failed
        return (
          <div className="bonus-display" style={{
            background: "rgba(255, 193, 7, 0.1)",
            border: `2px solid #ffc107`,
            borderRadius: "12px",
            padding: "2rem",
            marginTop: "1rem",
            textAlign: "center",
            animation: `${getAnimationStyle()} 0.6s ease-out`
          }}>
            <div style={{
              fontSize: "2.5rem",
              fontWeight: "bold",
              color: "#ffc107",
              marginBottom: "0.5rem"
            }}>
              ❌ No Boost
            </div>
            <div style={{
              color: "var(--color-text-secondary)",
              fontSize: "0.95rem",
              lineHeight: "1.5"
            }}>
              The shooting practice didn't click today. No bonus for your next game. Better luck next time!
            </div>
          </div>
        );
      } else if (skill === "shooting" && success) {
        // Shooting succeeded
        return (
          <div className="bonus-display" style={{
            background: "rgba(76, 175, 80, 0.1)",
            border: `2px solid ${accentColor}`,
            borderRadius: "12px",
            padding: "2rem",
            marginTop: "1rem",
            textAlign: "center",
            animation: `${getAnimationStyle()} 0.6s ease-out`
          }}>
          <div style={{
            fontSize: "2.5rem",
            fontWeight: "bold",
            color: "var(--color-text-main)",
            marginBottom: "0.5rem"
          }}>
            ✅ +{bonusValue}% Score Boost
          </div>
            <div style={{
              color: "var(--color-text-secondary)",
              fontSize: "0.95rem",
              lineHeight: "1.5"
            }}>
              Great shooting practice! Your team is hot and will get a <strong>{bonusValue}% score bonus</strong> next game!
            </div>
          </div>
        );
      } else if (skill === "passing") {
        // Passing (safe bonus)
        return (
          <div className="bonus-display" style={{
            background: "rgba(76, 175, 80, 0.1)",
            border: `2px solid ${accentColor}`,
            borderRadius: "12px",
            padding: "2rem",
            marginTop: "1rem",
            textAlign: "center",
            animation: `${getAnimationStyle()} 0.6s ease-out`
          }}>
            <div style={{
              fontSize: "2.5rem",
              fontWeight: "bold",
              color: "var(--color-text-main)",
              marginBottom: "0.5rem"
            }}>
              +{bonusValue}% Score Boost
            </div>
            <div style={{
              color: "var(--color-text-secondary)",
              fontSize: "0.95rem",
              lineHeight: "1.5"
            }}>
              Your team's spacing and ball movement are sharp. You've locked in a guaranteed <strong>{bonusValue}% score bonus</strong> for the next game!
            </div>
          </div>
        );
      }
    } else if (bonusType === "ppp_multiplier") {
      // Dribbling practice - PPP multiplier (e.g., 1.07 = 7% more efficient)
      const percentageBoost = Math.round((bonusValue - 1) * 100);
      return (
        <div className="bonus-display" style={{
          background: "rgba(33, 150, 243, 0.1)",
          border: `2px solid #2196f3`,
          borderRadius: "12px",
          padding: "2rem",
          marginTop: "1rem",
          textAlign: "center",
          animation: `${getAnimationStyle()} 0.6s ease-out`
        }}>
          <div style={{
            fontSize: "2.5rem",
            fontWeight: "bold",
            color: "var(--color-text-main)",
            marginBottom: "0.5rem"
          }}>
            +{percentageBoost}% Efficiency
          </div>
          <div style={{
            color: "var(--color-text-secondary)",
            fontSize: "0.95rem",
            lineHeight: "1.5"
          }}>
            Your dribbling drills have sharpened your efficiency. Your team will score more consistently with improved ball-handling next game - watch for higher scoring potential!
          </div>
        </div>
      );
    } else if (bonusType === "possession_bonus") {
      return (
        <div className="bonus-display" style={{
          background: "rgba(33, 150, 243, 0.1)",
          border: `2px solid #2196f3`,
          borderRadius: "12px",
          padding: "2rem",
          marginTop: "1rem",
          textAlign: "center",
          animation: `${getAnimationStyle()} 0.6s ease-out`
        }}>
          <div style={{
            fontSize: "2.5rem",
            fontWeight: "bold",
            color: "var(--color-text-main)",
            marginBottom: "0.5rem"
          }}>
            +{bonusValue}% Possession
          </div>
          <div style={{
            color: "var(--color-text-secondary)",
            fontSize: "0.95rem",
            lineHeight: "1.5"
          }}>
            Your dribbling drills have sharpened your ball-handling. You'll control the pace and get <strong>{bonusValue}% more possessions</strong> next game!
          </div>
        </div>
      );
    }
  };

  const handleDrillSelect = async (skill) => {
    if (stamina < 1) {
      setError("Not enough stamina to practice.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_URL}/api/practice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-ID": userId,
        },
        body: JSON.stringify({
          saveId,
          teamId,
          skill,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Practice failed");
      }

      const data = await res.json();
      
      setPracticeResult(data);
      setIsLoading(false);
    } catch (err) {
      // Practice error handled
      setError(err.message);
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleCloseModal}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Practice Center</h2>
          <button className="modal-close" onClick={handleCloseModal}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {practiceResult ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1.5rem", padding: "1rem 0" }}>
              {/* Result Header */}
              <div style={{ textAlign: "center", width: "100%" }}>
                <div style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  border: `3px solid ${accentColor}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "2rem",
                  margin: "0 auto 1rem"
                }}>
                  {skillIcons[practiceResult.skill]}
                </div>
                <p style={{ color: "var(--color-text-secondary)", margin: "0 0 0.5rem 0", fontWeight: "600", letterSpacing: "1px", textTransform: "uppercase", fontSize: "0.9rem" }}>
                  Practice Complete
                </p>
                <h1 style={{ color: "var(--color-text-main)", margin: "0 0 1rem 0", fontSize: "1.8rem", fontWeight: "700" }}>
                  {practiceResult.statLabel} Session
                </h1>
                <p style={{ color: "var(--color-text-main)", margin: 0, fontSize: "0.95rem", lineHeight: "1.5" }}>
                  {practiceResult.message}
                </p>
              </div>

              {/* Bonus Display */}
              {renderBonusDisplay()}
            </div>
          ) : (
            <>
              <p style={{ marginBottom: "1.5rem", color: "var(--color-text-main)" }}>
                Select a drill for your team. These bonuses will only apply to the next game played.
              </p>

              {stamina < 1 ? (
                <div
                  style={{
                    padding: "1.5rem",
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <p style={{ color: "rgba(239, 68, 68, 0.8)" }}>
                    Not enough stamina to practice. You have {stamina}/3 stamina.
                  </p>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                  {DRILLS.map((drill) => (
                    <button
                      key={drill.skill}
                      onClick={() => handleDrillSelect(drill.skill)}
                      disabled={isLoading}
                      style={{
                        padding: "1rem",
                        backgroundColor: hoveredDrill === drill.skill ? "rgba(18, 82, 210, 0.2)" : "rgba(18, 82, 210, 0.1)",
                        border: "2px solid var(--color-primary)",
                        borderRadius: "8px",
                        cursor: isLoading ? "not-allowed" : "pointer",
                        opacity: isLoading ? 0.6 : 1,
                        transition: "all 0.2s ease",
                        textAlign: "center",
                        color: "var(--color-text-main)",
                        transform: hoveredDrill === drill.skill ? "translateY(-2px)" : "translateY(0)",
                      }}
                      onMouseEnter={() => {
                        if (!isLoading) {
                          setHoveredDrill(drill.skill);
                        }
                      }}
                      onMouseLeave={() => {
                        setHoveredDrill(null);
                      }}
                    >
                      <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>{drill.icon}</div>
                      <div style={{ fontWeight: "600", marginBottom: "0.25rem", color: "var(--color-text-main)" }}>{drill.label}</div>
                      <div style={{ fontSize: "0.85rem", color: "var(--color-text-main)" }}>
                        {drill.subtitle}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {error && (
                <div
                  style={{
                    marginTop: "1rem",
                    padding: "1rem",
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: "8px",
                    color: "rgb(239, 68, 68)",
                  }}
                >
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="modal-btn modal-btn-secondary" 
            onClick={handleCloseModal} 
            disabled={isLoading}
          >
            {practiceResult ? "Close" : "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}

